import { useApolloClient } from '@apollo/client';
import { TFunction } from 'i18next';
import { FormEvent, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAgent, useFeature } from '@treams/treams-hooks';
import {
	AgentChatDocument,
	AgentChatMessageFragment,
	AgentChatMessageRole,
	AgentChatQuery,
	AgentChatsQuery,
	FeatureName,
	useAgentChatQuery,
} from '@treams/treams-network';

import { estimatePromptCostUsd } from '../estimateAgentChatCost';
import type { AgentChatSubject } from './AgentChatThread';
import { AgentChatThread } from './AgentChatThread';

const CHAT_TITLE_MAX_LENGTH = 80;

function deriveChatTitle(messages: AgentChatMessageFragment[], t: TFunction): string {
	const firstUserMessage = messages.find(
		(message) => message.role === AgentChatMessageRole.User,
	)?.content;

	const normalized = firstUserMessage?.replace(/\s+/g, ' ').trim();
	if (!normalized) {
		return t('aiAgent.newChat');
	}

	if (normalized.length <= CHAT_TITLE_MAX_LENGTH) {
		return normalized;
	}

	return `${normalized.slice(0, CHAT_TITLE_MAX_LENGTH - 1)}…`;
}

export type AgentChatRequest = {
	id: number;
	prompt: string;
	subjectID?: string;
};

export function AgentChatDetail(props: {
	chatID?: string;
	chats?: AgentChatsQuery['agentChats'];
	onChatPersisted: () => Promise<unknown>;
	embedded?: boolean;
	compact?: boolean;
	subject?: AgentChatSubject;
	startOverRef?: MutableRefObject<(() => void) | null>;
	request?: AgentChatRequest;
}) {
	const { onChatPersisted } = props;
	const [activeChatID, setActiveChatID] = useState(props.chatID);
	const chatID = props.embedded ? activeChatID : props.chatID;

	const { t, i18n } = useTranslation();
	const history = useHistory();
	const client = useApolloClient();
	const hasAdvancedAgent = useFeature(FeatureName.AdvancedAgent);
	const persistedChatIDRef = useRef<string>();
	const {
		send,
		messages: streamingMessages,
		chatID: streamedChatID,
		isStreaming,
		error,
		reset,
	} = useAgent();

	const [draft, setDraft] = useState('');
	const [pendingMessages, setPendingMessages] = useState<AgentChatMessageFragment[]>([]);

	const chatQuery = useAgentChatQuery({
		variables: { id: chatID! },
		skip: !chatID,
	});

	useEffect(() => {
		if (isStreaming || !streamedChatID) {
			return;
		}

		if (!chatID) {
			if (persistedChatIDRef.current === streamedChatID) {
				return;
			}
			persistedChatIDRef.current = streamedChatID;

			const messages = [...pendingMessages, ...streamingMessages];
			const now = new Date();

			// Pre-seed the cache with the messages to avoid flicker on route change
			client.cache.writeQuery<AgentChatQuery>({
				query: AgentChatDocument,
				variables: { id: streamedChatID },
				data: {
					agentChat: {
						__typename: 'AgentChat',
						id: streamedChatID,
						title: deriveChatTitle(messages, t),
						locale: i18n.language,
						createdAt: now,
						updatedAt: now,
						messages: messages.map((message) => ({
							__typename: 'AgentChatMessage' as const,
							id: message.id,
							role: message.role,
							content: message.content ?? null,
							toolCallId: message.toolCallId ?? null,
							createdAt: message.createdAt,
							toolCalls:
								message.toolCalls?.map((toolCall) => ({
									__typename: 'AgentChatToolCall' as const,
									id: toolCall.id,
									name: toolCall.name,
									arguments: toolCall.arguments ?? null,
								})) ?? null,
						})),
						usage: null,
					},
				},
			});

			if (props.embedded) {
				setActiveChatID(streamedChatID);
				setPendingMessages([]);
				reset();
				void onChatPersisted();
				return;
			}

			onChatPersisted().then(() => history.replace(`/assistant/${streamedChatID}`));
			return;
		}

		chatQuery.refetch().then(() => {
			setPendingMessages([]);
			reset();
		});
	}, [isStreaming, streamedChatID]);

	const messages = useMemo(
		() => [...(chatQuery.data?.agentChat.messages ?? []), ...pendingMessages, ...streamingMessages],
		[chatQuery.data?.agentChat.messages, pendingMessages, streamingMessages],
	);

	const usage = chatQuery.data?.agentChat.usage;
	const estimatedCost = useMemo(() => (usage ? estimatePromptCostUsd(usage) : undefined), [usage]);
	const subject = useMemo<AgentChatSubject>(() => {
		if (props.subject) {
			return props.subject;
		}

		if (!chatID) {
			return null;
		}

		return props.chats?.find((chat) => chat.id === chatID)?.subject ?? null;
	}, [chatID, props.chats, props.subject]);

	async function submitMessage(
		rawMessage: string,
		options?: { subjectID?: string; explicitSubject?: boolean; newChat?: boolean },
	) {
		const message = rawMessage.trim();
		if (!message || (isStreaming && !options?.newChat)) {
			return;
		}

		const subjectID = options?.explicitSubject
			? options.subjectID
			: (options?.subjectID ?? props.subject?.id);

		const createdAt = new Date();
		setDraft('');
		setPendingMessages((prev) => [
			...prev,
			{
				id: `pending-user-${createdAt.getTime()}`,
				role: AgentChatMessageRole.User,
				content: message,
				createdAt,
			},
		]);

		await send(message, {
			chatID: options?.newChat ? undefined : chatID,
			locale: i18n.language,
			subjectID,
		});
	}

	function handleSubmit(event: FormEvent) {
		event.preventDefault();
		void submitMessage(draft);
	}

	function startOver() {
		if (props.embedded) {
			persistedChatIDRef.current = undefined;
			setActiveChatID(undefined);
			setPendingMessages([]);
			setDraft('');
			reset();
			return;
		}

		history.push('/assistant/new');
	}

	if (props.startOverRef) {
		props.startOverRef.current = startOver;
	}

	useEffect(() => {
		if (!props.request) {
			return;
		}

		startOver();
		void submitMessage(props.request.prompt, {
			subjectID: props.request.subjectID,
			explicitSubject: true,
			newChat: true,
		});
	}, [props.request?.id]);

	return (
		<AgentChatThread
			messages={messages}
			isLoading={!!chatID && !chatQuery.data && !chatQuery.error}
			isStreaming={isStreaming}
			error={error ?? chatQuery.error?.message}
			draft={draft}
			onDraftChange={setDraft}
			onSubmit={handleSubmit}
			onSelectSuggestion={(message, subjectID) =>
				submitMessage(message, { subjectID, explicitSubject: true })
			}
			onStartOver={startOver}
			showComposer={hasAdvancedAgent}
			compact={props.compact}
			estimatedCost={estimatedCost}
			subject={subject}
		/>
	);
}
